import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import flowRight from 'lodash/flowRight';
import gettext from 'js-utils/gettext';

import withOverlayControlsHOC from 'eventbrite_design_system/structure/hoc/withOverlayControls';
import withMainControlsHOC from 'eventbrite_design_system/structure/hoc/withMainControls';

import Button from 'eventbrite_design_system/button/Button';
import CopyField from 'eventbrite_design_system/copyField/CopyField';
import LoadingOverlay from 'eventbrite_design_system/loadingOverlay/LoadingOverlay';

import Icon from 'eventbrite_design_system/icon/Icon';
import CheckChunkySvg from 'eventbrite_design_system/iconography/icons/CheckChunky';
import TrashSvg from 'eventbrite_design_system/iconography/icons/Trash';
import ChevronRightChunkySvg from 'eventbrite_design_system/iconography/icons/ChevronRightChunky';

import './ownerActionBar.scss';
import {
    COPY_SHARE_LINK_GA_ACTION,
    SHARE_COLLECTION_GA_ACTION,
    EDIT_COLLECTION_GA_ACTION,
} from '../constants';

const EditModeControls = ({ onEditCancel, onSave, isSaveDisabled }) => (
    <div className="collection-action-bar collection-action-bar__actions--edit-mode">
        <Button
            onClick={onEditCancel}
            style="link"
            __containerClassName="collection-action-bar__btn eds-l-mar-right-6"
        >
            {gettext('Cancel')}
        </Button>
        <Button
            onClick={onSave}
            style="neutral"
            disabled={isSaveDisabled}
            __containerClassName="collection-action-bar__btn"
        >
            {gettext('Save')}
        </Button>
    </div>
);

const NonEditModeControls = ({ onShareClick, onEditClick, isEditorial }) => {
    if (isEditorial) {
        return (
            <div className="user-collection__edit-btn-container eds-l-pad-vert-4">
                <Button onClick={onEditClick} style="neutral" size="block">
                    {gettext('Edit Collection')}
                </Button>
            </div>
        );
    }
    return (
        <div className="collection-action-bar">
            <Button
                __containerClassName="collection-action-bar__btn eds-l-mar-right-6"
                onClick={onEditClick}
                style="neutral"
            >
                {gettext('Edit Collection')}
            </Button>
            <Button
                __containerClassName="collection-action-bar__btn"
                onClick={onShareClick}
                style="neutral"
            >
                {gettext('Share')}
            </Button>
        </div>
    );
};

//eslint-disable-next-line
class ShareModalView extends PureComponent {
    static propTypes = {
        shareUrl: PropTypes.string,
        onNotifyAction: PropTypes.func,
        getShareUrl: PropTypes.func,
        onCloseModal: PropTypes.func,
    };

    state = {
        isResetShareView: false,
    };

    componentDidMount() {
        const { shareUrl, getShareUrl } = this.props;

        if (!shareUrl) {
            getShareUrl();
        }
    }

    _handleShowResetShareView = () => {
        this.setState({
            isResetShareView: true,
        });
    };

    _handleResetShareClick = () => {
        const { getShareUrl, onCloseModal, onNotifyAction } = this.props;

        getShareUrl();
        onNotifyAction(true);
        onCloseModal();
    };

    _handleCopyClick = () => {
        const {
            collectionId,
            onNotifyAction,
            onCloseModal,
            trackEvent,
        } = this.props;

        trackEvent(COPY_SHARE_LINK_GA_ACTION, collectionId);
        onNotifyAction();
        onCloseModal();
    };

    render() {
        const { shareUrl, onCloseModal } = this.props;

        const { isResetShareView } = this.state;

        let content = (
            <div className="eds-l-mar-vert-12">
                <div className="share-view__loading-overlay">
                    <LoadingOverlay size="large-thin" isShown={true} />
                </div>
                <div className="eds-text-bl eds-text--center eds-l-pad-top-10">
                    {gettext('Creating a unique share link for you...')}
                </div>
            </div>
        );

        if (shareUrl) {
            content = (
                <div>
                    <CopyField
                        inputFieldId="copy-field-collection-share-url"
                        label={gettext('Collection URL')}
                        value={shareUrl}
                        onCopyClick={this._handleCopyClick}
                    />
                    <div className="eds-text--center eds-l-mar-top-6 eds-l-mar-hor-6">
                        {gettext(
                            'Only people with this URL will be able to see your collection and the events in it.',
                        )}
                    </div>
                    <div className="eds-l-mar-top-12 eds-align--center">
                        <Button
                            onClick={this._handleShowResetShareView}
                            style="link"
                        >
                            {gettext('Turn off sharing for this collection')}
                            <Icon type={<ChevronRightChunkySvg />} />
                        </Button>
                    </div>
                </div>
            );
        }

        if (isResetShareView) {
            content = (
                <div className="eds-text--center">
                    <div>
                        <Icon type={<TrashSvg />} hasHalo={true} />
                    </div>
                    <h2 className="eds-l-bl eds-l-mar-top-2">
                        {gettext('Are you sure?')}
                    </h2>
                    <p className="eds-l-mar-top-4 eds-l-pad-hor-6">
                        {gettext(
                            "This link won't work anymore if you turn off sharing, but we'll make you a new one if you want to share it again afterwards.",
                        )}
                    </p>
                    <div className="eds-align--center eds-l-pad-top-4">
                        <div className="eds-l-pad-right-3">
                            <Button onClick={onCloseModal}>
                                {gettext('Nevermind')}
                            </Button>
                        </div>
                        <Button onClick={this._handleResetShareClick}>
                            {gettext('Turn off')}
                        </Button>
                    </div>
                </div>
            );
        }

        return content;
    }
}

//eslint-disable-next-line
class OwnerActionBar extends PureComponent {
    static propTypes = {
        shareUrl: PropTypes.string,
        saveCollectionEdits: PropTypes.func,
        showOverlay: PropTypes.func,
        isSaveDisabled: PropTypes.bool.isRequired,
        onCancel: PropTypes.func,
        isEditorial: PropTypes.bool,
    };

    componentDidUpdate(prevProps) {
        // Necessary to ensure that the content of
        // the modal updates when new props come through
        if (this.props.shareUrl && !prevProps.shareUrl) {
            this._handleShareClick();
        }
    }

    _handleShareClick = () => {
        const {
            collectionId,
            shareUrl,
            getShareUrl,
            showOverlay,
            trackEvent,
        } = this.props;

        trackEvent(SHARE_COLLECTION_GA_ACTION, collectionId);

        showOverlay('modal', {
            type: 'simple',
            title: gettext('Share with friends'),
            noMinHeight: true,
            isClosable: true,
            onClose: () => ({}),
            children: (
                <ShareModalView
                    collectionId={collectionId}
                    shareUrl={shareUrl}
                    onNotifyAction={this._handleShareNotify}
                    getShareUrl={getShareUrl}
                    onCloseModal={this._handleModalClose}
                    trackEvent={trackEvent}
                />
            ),
        });
    };

    _handleModalClose = () => {
        const { showOverlay } = this.props;

        showOverlay(null);
    };

    _handleShareNotify = (isReset = false) => {
        const { addMainNotification } = this.props;

        let config = {
            iconType: <CheckChunkySvg />,
            children: gettext('Link copied to clipboard'),
        };

        if (isReset) {
            config = {
                children: gettext('Share link has been reset'),
            };
        }

        addMainNotification({
            hasCloseButton: true,
            ...config,
        });
    };

    _handleEditClick = () => {
        const { collectionId, toggleEditMode, trackEvent } = this.props;

        trackEvent(EDIT_COLLECTION_GA_ACTION, collectionId);
        toggleEditMode();
    };

    _handleEditCancel = () => {
        this.props.onCancel();
        this.props.toggleEditMode();
    };

    render() {
        const {
            isEditMode,
            saveCollectionEdits,
            isSaveDisabled,
            isEditorial,
        } = this.props;

        let Controls = NonEditModeControls;

        if (isEditMode) {
            Controls = EditModeControls;
        }

        return (
            <Controls
                onEditClick={this._handleEditClick}
                onEditCancel={this._handleEditCancel}
                onSave={saveCollectionEdits}
                onShareClick={this._handleShareClick}
                isSaveDisabled={isSaveDisabled}
                isEditorial={isEditorial}
            />
        );
    }
}

const enhancedActionBar = flowRight(
    withOverlayControlsHOC,
    withMainControlsHOC,
);

export default enhancedActionBar(OwnerActionBar);
